import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Building2,
  CalendarDays,
  DollarSign,
  Info,
  Save,
  Users,
  X,
} from "lucide-react";
import { useTranslation } from "@/shared/providers/LanguageProvider";
import { useToast } from "@/shared/providers/ToastProvider";
import { ApiError } from "@/shared/lib/apiClient";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { FormField, inputClass, selectClass, textareaClass } from "../components/FormField";
import { conventionCoverageUnitOptions, conventionTreatmentTypeOptions, conventionBudgetPeriodicityOptions } from "../data";
import {
  createAdminConvention,
  fetchAdminConvention,
  fetchAdminConventionsMeta,
  updateAdminConvention,
  type AdminConventionMeta,
} from "../api/adminApi";
import { getAuthToken } from "../auth";
import { cn } from "@/shared/lib/cn";

type DependentType = "spouse" | "children" | "parents" | "other";
type CoverageUnit = "personne" | "famille" | "mixte";
type BudgetPeriodicity = "trimestriel" | "semestriel" | "annuel";

type ConventionFormState = {
  title: string;
  treatmentType: string;
  coverageUnit: CoverageUnit;
  startDate: string;
  endDate: string;
  description: string;
  specialConditions: string;
  companyId: string;
  coveredProviders: string[];
  providerForfaits: Record<string, string>;
  providerClientForfaits: Record<string, string>;
  totalBudget: string;
  budgetPeriodicity: BudgetPeriodicity;
  dailyCeiling: string;
  monthlyCeiling: string;
  annualCeiling: string;
  familyDailyCeiling: string;
  familyMonthlyCeiling: string;
  familyAnnualCeiling: string;
  monthlyPackageAmount: string;
  copayPct: string;
  globalBudgetAlertThreshold: string;
  individualAlertThreshold: string;
  childAgeLimit: string;
  studentChildAgeLimit: string;
  spouseAgeLimit: string;
  maxDependentsPerEmployee: string;
  coveredDependentTypes: DependentType[];
};

const initialFormState: ConventionFormState = {
  title: "",
  treatmentType: "",
  coverageUnit: "famille",
  startDate: "",
  endDate: "",
  description: "",
  specialConditions: "",
  companyId: "",
  coveredProviders: [],
  providerForfaits: {},
  providerClientForfaits: {},
  totalBudget: "",
  budgetPeriodicity: "annuel",
  dailyCeiling: "",
  monthlyCeiling: "",
  annualCeiling: "",
  familyDailyCeiling: "",
  familyMonthlyCeiling: "",
  familyAnnualCeiling: "",
  monthlyPackageAmount: "",
  copayPct: "0",
  globalBudgetAlertThreshold: "",
  individualAlertThreshold: "",
  childAgeLimit: "21",
  studentChildAgeLimit: "26",
  spouseAgeLimit: "",
  maxDependentsPerEmployee: "5",
  coveredDependentTypes: [],
};

const multiSelectClass =
  "min-h-[280px] w-full rounded-lg border border-border bg-card px-3 py-2 text-sm text-foreground outline-none transition-colors focus:border-primary focus:ring-2 focus:ring-primary/20";

const affixInputClass =
  "h-10 w-full border border-border bg-card px-3 text-sm text-foreground outline-none transition-colors focus:border-primary focus:ring-2 focus:ring-primary/20";

const affixClass =
  "flex h-10 shrink-0 items-center border border-border bg-card-muted px-3 text-sm text-muted-foreground";

type PrefixInputProps = {
  prefix: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
  min?: string;
};

function PrefixInput({ prefix, value, onChange, type = "text", min }: PrefixInputProps) {
  return (
    <div className="flex">
      <span className={cn(affixClass, "rounded-l-lg border-r-0")}>{prefix}</span>
      <input
        type={type}
        min={min}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className={cn(affixInputClass, "rounded-r-lg")}
      />
    </div>
  );
}

type SuffixInputProps = {
  suffix: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  type?: string;
  min?: string;
  max?: string;
};

function SuffixInput({
  suffix,
  value,
  onChange,
  placeholder,
  type = "text",
  min,
  max,
}: SuffixInputProps) {
  return (
    <div className="flex">
      <input
        type={type}
        min={min}
        max={max}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        className={cn(affixInputClass, "rounded-l-lg border-r-0")}
      />
      <span className={cn(affixClass, "rounded-r-lg border-l-0")}>{suffix}</span>
    </div>
  );
}

export function ConventionFormPage({ mode }: { mode: "create" | "edit" }) {
  const { t } = useTranslation();
  const labels = t.safecheck.conventions.create;
  const { showToast } = useToast();
  const navigate = useNavigate();
  const params = useParams();
  const conventionId = mode === "edit" ? Number(params.id) : null;

  const [form, setForm] = useState<ConventionFormState>(initialFormState);
  const [meta, setMeta] = useState<AdminConventionMeta>({ companies: [], providers: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showValidation, setShowValidation] = useState(false);

  const dependentTypeKeys: DependentType[] = ["spouse", "children", "parents", "other"];

  const updateForm = <K extends keyof ConventionFormState>(
    key: K,
    value: ConventionFormState[K],
  ) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  const handleProvidersChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selected = Array.from(event.target.selectedOptions).map((option) => option.value);
    setForm((current) => {
      const providerForfaits = { ...current.providerForfaits };
      const providerClientForfaits = { ...current.providerClientForfaits };
      for (const id of selected) {
        if (!(id in providerForfaits)) providerForfaits[id] = "";
        if (!(id in providerClientForfaits)) providerClientForfaits[id] = "";
      }
      for (const id of Object.keys(providerForfaits)) {
        if (!selected.includes(id)) {
          delete providerForfaits[id];
          delete providerClientForfaits[id];
        }
      }
      return {
        ...current,
        coveredProviders: selected,
        providerForfaits,
        providerClientForfaits,
      };
    });
  };

  const toggleDependentType = (type: DependentType) => {
    setForm((current) => ({
      ...current,
      coveredDependentTypes: current.coveredDependentTypes.includes(type)
        ? current.coveredDependentTypes.filter((item) => item !== type)
        : [...current.coveredDependentTypes, type],
    }));
  };

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      if (!getAuthToken()) {
        setError("Connexion admin requise.");
        setLoading(false);
        return;
      }

      try {
        const formMeta = await fetchAdminConventionsMeta();
        if (cancelled) return;
        setMeta(formMeta);

        if (mode === "edit" && conventionId && !Number.isNaN(conventionId)) {
          const convention = await fetchAdminConvention(conventionId);
          if (cancelled) return;
          const covered: DependentType[] = [];
          if (convention.covers_spouse) covered.push("spouse");
          if (convention.covers_children) covered.push("children");
          if (convention.covers_parents) covered.push("parents");
          if (convention.covers_other) covered.push("other");
          const providerForfaits: Record<string, string> = {};
          const providerClientForfaits: Record<string, string> = {};
          for (const provider of convention.providers ?? []) {
            const id = String(provider.id);
            providerForfaits[id] =
              provider.forfait != null ? String(provider.forfait) : "";
            providerClientForfaits[id] =
              provider.client_forfait != null ? String(provider.client_forfait) : "";
          }
          setForm({
            title: convention.name ?? "",
            treatmentType: convention.type ?? "",
            coverageUnit:
              convention.coverage_unit === "personne" ||
              convention.coverage_unit === "mixte" ||
              convention.coverage_unit === "famille"
                ? convention.coverage_unit
                : "famille",
            startDate: convention.date_debut ?? "",
            endDate: convention.date_fin ?? "",
            description: convention.description ?? "",
            specialConditions: convention.special_conditions ?? "",
            companyId: String(convention.company_id || ""),
            coveredProviders: (convention.provider_ids ?? []).map(String),
            providerForfaits,
            providerClientForfaits,
            totalBudget: convention.budget ? String(convention.budget) : "",
            budgetPeriodicity:
              convention.budget_periodicity === "trimestriel" ||
              convention.budget_periodicity === "semestriel" ||
              convention.budget_periodicity === "annuel"
                ? convention.budget_periodicity
                : "annuel",
            dailyCeiling:
              convention.daily_ceiling != null ? String(convention.daily_ceiling) : "",
            monthlyCeiling:
              convention.monthly_ceiling != null ? String(convention.monthly_ceiling) : "",
            annualCeiling:
              convention.annual_ceiling != null
                ? String(convention.annual_ceiling)
                : convention.individual_ceiling != null
                  ? String(convention.individual_ceiling)
                  : "",
            familyDailyCeiling:
              convention.family_daily_ceiling != null
                ? String(convention.family_daily_ceiling)
                : "",
            familyMonthlyCeiling:
              convention.family_monthly_ceiling != null
                ? String(convention.family_monthly_ceiling)
                : "",
            familyAnnualCeiling:
              convention.family_annual_ceiling != null
                ? String(convention.family_annual_ceiling)
                : "",
            monthlyPackageAmount:
              convention.monthly_package_amount != null
                ? String(convention.monthly_package_amount)
                : "",
            copayPct: String(convention.copay_pct ?? 0),
            globalBudgetAlertThreshold: String(convention.global_budget_alert_threshold ?? ""),
            individualAlertThreshold:
              convention.individual_alert_threshold != null
                ? String(convention.individual_alert_threshold)
                : "",
            childAgeLimit: String(convention.child_age_limit ?? 21),
            studentChildAgeLimit: String(convention.student_child_age_limit ?? 26),
            spouseAgeLimit:
              convention.spouse_age_limit != null ? String(convention.spouse_age_limit) : "",
            maxDependentsPerEmployee: String(convention.max_dependents_per_employee ?? 5),
            coveredDependentTypes: covered,
          });
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Impossible de charger le formulaire.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [mode, conventionId]);

  const isValid =
    form.title.trim() &&
    form.treatmentType &&
    form.startDate &&
    form.endDate &&
    form.companyId &&
    form.coveredProviders.length > 0;

  const toNumberOrNull = (value: string) => {
    const trimmed = value.trim();
    if (!trimmed) return null;
    const n = Number(trimmed);
    return Number.isFinite(n) ? n : null;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!isValid) {
      setShowValidation(true);
      return;
    }
    if (!getAuthToken()) {
      setError("Connexion admin requise.");
      return;
    }

    setShowValidation(false);
    setSaving(true);
    setError(null);

    const providerForfaits: Record<string, number | null> = {};
    const providerClientForfaits: Record<string, number | null> = {};
    for (const id of form.coveredProviders) {
      providerForfaits[id] = toNumberOrNull(form.providerForfaits[id] ?? "");
      providerClientForfaits[id] = toNumberOrNull(form.providerClientForfaits[id] ?? "");
    }

    const payload = {
      title: form.title.trim(),
      company_id: Number(form.companyId),
      treatment_type: form.treatmentType,
      coverage_unit: form.coverageUnit,
      start_date: form.startDate,
      end_date: form.endDate,
      description: form.description,
      special_conditions: form.specialConditions,
      total_budget: toNumberOrNull(form.totalBudget),
      budget_periodicity: form.budgetPeriodicity,
      daily_ceiling: toNumberOrNull(form.dailyCeiling),
      monthly_ceiling: toNumberOrNull(form.monthlyCeiling),
      annual_ceiling: toNumberOrNull(form.annualCeiling),
      family_daily_ceiling: toNumberOrNull(form.familyDailyCeiling),
      family_monthly_ceiling: toNumberOrNull(form.familyMonthlyCeiling),
      family_annual_ceiling: toNumberOrNull(form.familyAnnualCeiling),
      individual_ceiling: toNumberOrNull(
        form.coverageUnit === "famille" ? form.familyAnnualCeiling : form.annualCeiling,
      ),
      monthly_package_amount: toNumberOrNull(form.monthlyPackageAmount),
      copay_pct: toNumberOrNull(form.copayPct) ?? 0,
      global_budget_alert_threshold: toNumberOrNull(form.globalBudgetAlertThreshold),
      individual_alert_threshold: toNumberOrNull(form.individualAlertThreshold),
      child_age_limit: toNumberOrNull(form.childAgeLimit),
      student_child_age_limit: toNumberOrNull(form.studentChildAgeLimit),
      spouse_age_limit: toNumberOrNull(form.spouseAgeLimit),
      max_dependents_per_employee: toNumberOrNull(form.maxDependentsPerEmployee),
      covered_dependent_types: form.coveredDependentTypes,
      provider_ids: form.coveredProviders.map(Number).filter((id) => id > 0),
      provider_forfaits: providerForfaits,
      provider_client_forfaits: providerClientForfaits,
    };

    try {
      if (mode === "edit" && conventionId) {
        await updateAdminConvention(conventionId, payload);
        showToast(t.actions.saved);
      } else {
        await createAdminConvention(payload);
        showToast(t.actions.saved);
      }
      navigate("/dashboard/conventions");
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Enregistrement impossible.";
      setError(message);
      showToast(message);
    } finally {
      setSaving(false);
    }
  };

  const optionalLabel = (field: string) => `${field} ${labels.optional}`;
  const pageTitle = mode === "edit" ? labels.editTitle : labels.title;

  if (loading) {
    return <p className="text-sm text-muted">Chargement…</p>;
  }

  return (
    <>
      <Breadcrumbs
        items={[
          { label: t.safecheck.conventions.title, href: "/dashboard/conventions" },
          { label: pageTitle },
        ]}
      />

      <div className="mb-4 flex justify-end">
        <Link
          to="/dashboard/conventions"
          className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-card-muted"
        >
          <ArrowLeft size={14} />
          {labels.back}
        </Link>
      </div>

      {error && (
        <div className="mb-4 rounded-lg border border-pink/30 bg-pink-soft px-4 py-3 text-sm text-pink">
          {error}
        </div>
      )}

      <form onSubmit={(event) => void handleSubmit(event)} className="space-y-6">
        <div className="theme-card p-6">
          <div className="mb-5 flex items-center gap-2 text-sm font-semibold text-foreground">
            <Info size={16} className="text-primary" />
            {labels.generalInfo}
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <FormField label={labels.fields.title} required className="md:col-span-3">
              <input
                value={form.title}
                onChange={(event) => updateForm("title", event.target.value)}
                placeholder={labels.placeholders.title}
                className={inputClass}
              />
              {showValidation && !form.title.trim() && (
                <p className="mt-1 text-xs text-pink">{labels.requiredField}</p>
              )}
            </FormField>

            <FormField label={labels.fields.treatmentType} required>
              <select
                value={form.treatmentType}
                onChange={(event) => updateForm("treatmentType", event.target.value)}
                className={selectClass}
              >
                <option value="">{labels.placeholders.treatmentType}</option>
                {conventionTreatmentTypeOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {labels.treatmentTypes[option.value as keyof typeof labels.treatmentTypes]}
                  </option>
                ))}
              </select>
              {showValidation && !form.treatmentType && (
                <p className="mt-1 text-xs text-pink">{labels.requiredField}</p>
              )}
            </FormField>

            <FormField label={labels.fields.startDate} required>
              <div className="relative">
                <CalendarDays
                  size={14}
                  className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted"
                />
                <input
                  type="date"
                  value={form.startDate}
                  onChange={(event) => updateForm("startDate", event.target.value)}
                  placeholder={labels.placeholders.date}
                  className={`${inputClass} pl-9`}
                />
              </div>
              {showValidation && !form.startDate && (
                <p className="mt-1 text-xs text-pink">{labels.requiredField}</p>
              )}
            </FormField>

            <FormField label={labels.fields.endDate} required>
              <div className="relative">
                <CalendarDays
                  size={14}
                  className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted"
                />
                <input
                  type="date"
                  value={form.endDate}
                  onChange={(event) => updateForm("endDate", event.target.value)}
                  placeholder={labels.placeholders.date}
                  className={`${inputClass} pl-9`}
                />
              </div>
              {showValidation && !form.endDate && (
                <p className="mt-1 text-xs text-pink">{labels.requiredField}</p>
              )}
            </FormField>

            <FormField label={labels.fields.description} className="md:col-span-3">
              <textarea
                value={form.description}
                onChange={(event) => updateForm("description", event.target.value)}
                placeholder={labels.placeholders.description}
                className={textareaClass}
              />
            </FormField>

            <FormField label={labels.fields.specialConditions} className="md:col-span-3">
              <textarea
                value={form.specialConditions}
                onChange={(event) => updateForm("specialConditions", event.target.value)}
                placeholder={labels.placeholders.specialConditions}
                className={textareaClass}
              />
            </FormField>
          </div>
        </div>

        <div className="theme-card p-6">
          <div className="mb-5 flex items-center gap-2 text-sm font-semibold text-foreground">
            <Building2 size={16} className="text-primary" />
            {labels.contractingParties}
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <FormField label={labels.fields.company} required>
              <select
                value={form.companyId}
                onChange={(event) => updateForm("companyId", event.target.value)}
                className={selectClass}
              >
                <option value="">{labels.placeholders.company}</option>
                {meta.companies.map((company) => (
                  <option key={company.id} value={String(company.id)}>
                    {company.name}
                  </option>
                ))}
              </select>
              {showValidation && !form.companyId && (
                <p className="mt-1 text-xs text-pink">{labels.requiredField}</p>
              )}
            </FormField>

            <FormField
              label={`${labels.fields.coveredProviders} ${labels.fields.coveredProvidersHint}`}
              required
              hint={labels.hints.coveredProviders}
            >
              <select
                multiple
                value={form.coveredProviders}
                onChange={handleProvidersChange}
                className={multiSelectClass}
              >
                {meta.providers.map((provider) => (
                  <option key={provider.id} value={String(provider.id)}>
                    {provider.city
                      ? `${provider.commercial_name} (${provider.type}) — ${provider.city}`
                      : `${provider.commercial_name} (${provider.type})`}
                  </option>
                ))}
              </select>
              {showValidation && form.coveredProviders.length === 0 && (
                <p className="mt-1 text-xs text-pink">{labels.requiredField}</p>
              )}
            </FormField>
          </div>

          {(form.treatmentType === "forfaitaire" || form.treatmentType === "mixte") &&
            form.coveredProviders.length > 0 && (
              <div className="mt-6 border-t border-border pt-6">
                <p className="mb-4 text-sm font-semibold text-foreground">
                  {labels.providerForfaitSection}
                </p>
                <p className="mb-4 text-xs text-muted">{labels.hints.providerForfaits}</p>
                <div className="space-y-3">
                  {form.coveredProviders.map((providerId) => {
                    const provider = meta?.providers.find((p) => String(p.id) === providerId);
                    const label = provider
                      ? provider.city
                        ? `${provider.commercial_name} (${provider.type}) — ${provider.city}`
                        : `${provider.commercial_name} (${provider.type})`
                      : `#${providerId}`;

                    return (
                      <div
                        key={providerId}
                        className="grid grid-cols-1 gap-3 rounded-lg border border-border bg-card-muted/20 p-4 md:grid-cols-[1fr_160px_160px]"
                      >
                        <p className="self-center text-sm font-medium text-foreground">{label}</p>
                        <FormField label={labels.fields.providerForfait}>
                          <PrefixInput
                            prefix="$"
                            value={form.providerForfaits[providerId] ?? ""}
                            onChange={(value) =>
                              setForm((current) => ({
                                ...current,
                                providerForfaits: {
                                  ...current.providerForfaits,
                                  [providerId]: value,
                                },
                              }))
                            }
                            type="number"
                            min="0"
                          />
                        </FormField>
                        <FormField label={labels.fields.providerClientForfait}>
                          <PrefixInput
                            prefix="$"
                            value={form.providerClientForfaits[providerId] ?? ""}
                            onChange={(value) =>
                              setForm((current) => ({
                                ...current,
                                providerClientForfaits: {
                                  ...current.providerClientForfaits,
                                  [providerId]: value,
                                },
                              }))
                            }
                            type="number"
                            min="0"
                          />
                        </FormField>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
        </div>

        <div className="theme-card p-6">
          <div className="mb-5 flex items-center gap-2 text-sm font-semibold text-foreground">
            <DollarSign size={16} className="text-primary" />
            {labels.financialParams}
          </div>

          <div className="mb-5 grid grid-cols-1 gap-4 md:grid-cols-2">
            <FormField label={labels.fields.coverageUnit} required hint={labels.hints.coverageUnit}>
              <div className="space-y-2 rounded-lg border border-border bg-card-muted/20 p-3">
                {conventionCoverageUnitOptions.map((option) => (
                  <label key={option.value} className="flex cursor-pointer items-start gap-2 text-sm">
                    <input
                      type="radio"
                      name="coverageUnit"
                      value={option.value}
                      checked={form.coverageUnit === option.value}
                      onChange={() =>
                        updateForm("coverageUnit", option.value as CoverageUnit)
                      }
                      className="mt-1"
                    />
                    <span>
                      <span className="font-medium text-foreground">
                        {labels.coverageUnits[option.value as CoverageUnit]}
                      </span>
                      <span className="mt-0.5 block text-xs text-muted">
                        {labels.coverageUnitHints[option.value as CoverageUnit]}
                      </span>
                    </span>
                  </label>
                ))}
              </div>
            </FormField>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
            <FormField label={optionalLabel(labels.fields.totalBudget)}>
              <PrefixInput
                prefix="$"
                value={form.totalBudget}
                onChange={(value) => updateForm("totalBudget", value)}
                type="number"
                min="0"
              />
            </FormField>

            <FormField
              label={labels.fields.budgetPeriodicity}
              required
              hint={labels.hints.budgetPeriodicity}
            >
              <select
                className={selectClass}
                value={form.budgetPeriodicity}
                onChange={(event) =>
                  updateForm("budgetPeriodicity", event.target.value as BudgetPeriodicity)
                }
              >
                {conventionBudgetPeriodicityOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {labels.budgetPeriodicities[option.value as BudgetPeriodicity]}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-muted">
                {labels.budgetPeriodicityHints[form.budgetPeriodicity]}
              </p>
            </FormField>

            <FormField label={labels.fields.monthlyPackageAmount}>
              <PrefixInput
                prefix="$"
                value={form.monthlyPackageAmount}
                onChange={(value) => updateForm("monthlyPackageAmount", value)}
                type="number"
                min="0"
              />
            </FormField>

            <FormField label={labels.fields.copayPct}>
              <SuffixInput
                suffix={labels.suffixes.percent}
                value={form.copayPct}
                onChange={(value) => updateForm("copayPct", value)}
                type="number"
                min="0"
              />
            </FormField>

            <FormField label={labels.fields.globalBudgetAlertThreshold} hint={labels.hints.globalBudgetAlert}>
              <SuffixInput
                suffix={labels.suffixes.percent}
                value={form.globalBudgetAlertThreshold}
                onChange={(value) => updateForm("globalBudgetAlertThreshold", value)}
                placeholder={labels.placeholders.globalBudgetAlertThreshold}
                type="number"
                min="0"
                max="100"
              />
            </FormField>

            <FormField
              label={optionalLabel(labels.fields.individualAlertThreshold)}
              hint={labels.hints.individualAlert}
            >
              <SuffixInput
                suffix={labels.suffixes.percent}
                value={form.individualAlertThreshold}
                onChange={(value) => updateForm("individualAlertThreshold", value)}
                placeholder={labels.placeholders.individualAlertThreshold}
                type="number"
                min="0"
                max="100"
              />
            </FormField>
          </div>

          {(form.coverageUnit === "personne" || form.coverageUnit === "mixte") && (
            <div className="mt-6 border-t border-border pt-5">
              <p className="mb-3 text-sm font-semibold text-foreground">
                {labels.personCeilingsTitle}
              </p>
              <p className="mb-4 text-xs text-muted">{labels.hints.personCeilings}</p>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <FormField label={optionalLabel(labels.fields.dailyCeiling)}>
                  <PrefixInput
                    prefix="$"
                    value={form.dailyCeiling}
                    onChange={(value) => updateForm("dailyCeiling", value)}
                    type="number"
                    min="0"
                  />
                </FormField>
                <FormField label={optionalLabel(labels.fields.monthlyCeiling)}>
                  <PrefixInput
                    prefix="$"
                    value={form.monthlyCeiling}
                    onChange={(value) => updateForm("monthlyCeiling", value)}
                    type="number"
                    min="0"
                  />
                </FormField>
                <FormField label={optionalLabel(labels.fields.annualCeiling)}>
                  <PrefixInput
                    prefix="$"
                    value={form.annualCeiling}
                    onChange={(value) => updateForm("annualCeiling", value)}
                    type="number"
                    min="0"
                  />
                </FormField>
              </div>
            </div>
          )}

          {(form.coverageUnit === "famille" || form.coverageUnit === "mixte") && (
            <div className="mt-6 border-t border-border pt-5">
              <p className="mb-3 text-sm font-semibold text-foreground">
                {labels.familyCeilingsTitle}
              </p>
              <p className="mb-4 text-xs text-muted">{labels.hints.familyCeilings}</p>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <FormField label={optionalLabel(labels.fields.familyDailyCeiling)}>
                  <PrefixInput
                    prefix="$"
                    value={form.familyDailyCeiling}
                    onChange={(value) => updateForm("familyDailyCeiling", value)}
                    type="number"
                    min="0"
                  />
                </FormField>
                <FormField label={optionalLabel(labels.fields.familyMonthlyCeiling)}>
                  <PrefixInput
                    prefix="$"
                    value={form.familyMonthlyCeiling}
                    onChange={(value) => updateForm("familyMonthlyCeiling", value)}
                    type="number"
                    min="0"
                  />
                </FormField>
                <FormField label={optionalLabel(labels.fields.familyAnnualCeiling)}>
                  <PrefixInput
                    prefix="$"
                    value={form.familyAnnualCeiling}
                    onChange={(value) => updateForm("familyAnnualCeiling", value)}
                    type="number"
                    min="0"
                  />
                </FormField>
              </div>
            </div>
          )}
        </div>

        <div className="theme-card p-6">
          <div className="mb-5 flex items-center gap-2 text-sm font-semibold text-foreground">
            <Users size={16} className="text-primary" />
            {labels.beneficiaryRules}
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
            <FormField label={labels.fields.childAgeLimit}>
              <SuffixInput
                suffix={labels.suffixes.years}
                value={form.childAgeLimit}
                onChange={(value) => updateForm("childAgeLimit", value)}
                placeholder={labels.placeholders.childAgeLimit}
                type="number"
                min="0"
              />
            </FormField>

            <FormField label={labels.fields.studentChildAgeLimit}>
              <SuffixInput
                suffix={labels.suffixes.years}
                value={form.studentChildAgeLimit}
                onChange={(value) => updateForm("studentChildAgeLimit", value)}
                placeholder={labels.placeholders.studentChildAgeLimit}
                type="number"
                min="0"
              />
            </FormField>

            <FormField label={optionalLabel(labels.fields.spouseAgeLimit)}>
              <SuffixInput
                suffix={labels.suffixes.years}
                value={form.spouseAgeLimit}
                onChange={(value) => updateForm("spouseAgeLimit", value)}
                placeholder={labels.placeholders.spouseAgeLimit}
                type="number"
                min="0"
              />
            </FormField>

            <FormField label={labels.fields.maxDependentsPerEmployee}>
              <input
                type="number"
                min="0"
                value={form.maxDependentsPerEmployee}
                onChange={(event) => updateForm("maxDependentsPerEmployee", event.target.value)}
                placeholder={labels.placeholders.maxDependentsPerEmployee}
                className={inputClass}
              />
            </FormField>
          </div>

          <div className="mt-4">
            <p className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              {labels.fields.coveredDependentTypes}
            </p>
            <div className="flex flex-wrap gap-6">
              {dependentTypeKeys.map((type) => (
                <label key={type} className="inline-flex cursor-pointer items-center gap-2 text-sm text-foreground">
                  <input
                    type="checkbox"
                    checked={form.coveredDependentTypes.includes(type)}
                    onChange={() => toggleDependentType(type)}
                    className="h-4 w-4 rounded border-border text-primary focus:ring-primary/20"
                  />
                  {labels.dependentTypes[type]}
                </label>
              ))}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3">
          <Link
            to="/dashboard/conventions"
            className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-5 py-2.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-card-muted"
          >
            <X size={16} />
            {labels.cancel}
          </Link>
          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-lg bg-primary px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-primary/90 disabled:opacity-60"
          >
            <Save size={16} />
            {saving ? "…" : labels.submit}
          </button>
        </div>
      </form>
    </>
  );
}

export function CreateConventionPage() {
  return <ConventionFormPage mode="create" />;
}

export function EditConventionPage() {
  return <ConventionFormPage mode="edit" />;
}
